import { supabase } from "@/integrations/supabase/client";
import type { Product, ProductImage, ProductVariant, ProductWithRelations, LegacyProduct } from "@/types/ecommerce";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

type ProductSaveErrorCode =
  | "session_expired"
  | "admin_required"
  | "product_not_found"
  | "rls_update_blocked"
  | "update_not_confirmed";

export class ProductSaveError extends Error {
  code: ProductSaveErrorCode;

  constructor(code: ProductSaveErrorCode, message: string) {
    super(message);
    this.name = "ProductSaveError";
    this.code = code;
  }
}

const assertAdminSession = async () => {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (sessionError || !session?.user) {
    throw new ProductSaveError(
      "session_expired",
      "Sua sessão expirou. Entre novamente no painel antes de salvar o produto.",
    );
  }

  const { data: isAdmin, error: roleError } = await supabase.rpc("has_role", {
    _user_id: session.user.id,
    _role: "admin",
  });
  if (roleError || !isAdmin) {
    throw new ProductSaveError(
      "admin_required",
      "Seu usuário não possui permissão de administrador para salvar produtos.",
    );
  }

  return session;
};

const diagnoseProductUpdate = async (id: string) => {
  const { data, error } = await (supabase as any).rpc("admin_product_update_diagnostics", {
    p_product_id: id,
  });
  if (error) {
    return { error };
  }
  const result = data as {
    authenticated?: boolean;
    is_admin?: boolean;
    exists?: boolean | null;
  } | null;
  return { result };
};

export interface ListProductsParams {
  categorySlug?: string;
  collectionSlug?: string;
  featuredOnly?: boolean;
  bestsellerOnly?: boolean;
  activeOnly?: boolean;
  categoryId?: string;
  collectionId?: string;
  search?: string;
  status?: "all" | "active" | "inactive";
  withRelations?: boolean;
}

export interface AdminProductRow extends Product {
  images?: Pick<ProductImage, "id" | "image_url" | "is_primary" | "sort_order">[];
  variants?: Pick<ProductVariant, "id" | "stock_quantity" | "is_active">[];
  category_ref?: { id: string; name: string; slug: string } | null;
  collection_ref?: { id: string; name: string; slug: string } | null;
  total_stock?: number;
  primary_image_url?: string | null;
}

export const productsService = {
  assertAdminSession,

  async list(params: ListProductsParams = {}): Promise<Product[]> {
    const select = params.withRelations
      ? "*, images:product_images(id,image_url,is_primary,sort_order), variants:product_variants(id,price,stock_quantity,is_active,color:product_colors(id,name,slug,hex_code),size:product_sizes(id,name,slug)), category_ref:categories!products_category_id_fkey(id,name,slug)"
      : "*";
    let query = supabase.from("products").select(select).order("created_at", { ascending: false });
    if (params.activeOnly !== false) query = query.eq("is_active", true);
    if (params.featuredOnly) query = query.eq("is_featured", true);
    if (params.bestsellerOnly) query = query.eq("is_bestseller", true);
    if (params.categoryId) query = query.eq("category_id", params.categoryId);
    if (params.collectionId) query = query.eq("collection_id", params.collectionId);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as unknown as Product[];
  },

  async listAdmin(params: ListProductsParams = {}): Promise<AdminProductRow[]> {
    let query = supabase
      .from("products")
      .select(
        "*, images:product_images(id,image_url,is_primary,sort_order), variants:product_variants(id,stock_quantity,is_active), category_ref:categories!products_category_id_fkey(id,name,slug), collection_ref:collections!products_collection_id_fkey(id,name,slug)"
      )
      .order("created_at", { ascending: false });

    if (params.status === "active") query = query.eq("is_active", true);
    else if (params.status === "inactive") query = query.eq("is_active", false);

    if (params.categoryId) query = query.eq("category_id", params.categoryId);
    if (params.collectionId) query = query.eq("collection_id", params.collectionId);
    if (params.search?.trim()) query = query.ilike("name", `%${params.search.trim()}%`);

    const { data, error } = await query;
    if (error) throw error;

    const rows = (data ?? []) as unknown as AdminProductRow[];
    return rows.map((row) => {
      const variants = row.variants ?? [];
      const total_stock = variants.reduce(
        (sum, v) => sum + (v.is_active === false ? 0 : v.stock_quantity ?? 0),
        0
      );
      const images = row.images ?? [];
      const primary =
        images.find((i) => i.is_primary)?.image_url ??
        [...images].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0]?.image_url ??
        row.image_url ??
        null;
      return { ...row, total_stock, primary_image_url: primary } as AdminProductRow;
    });
  },

  async getById(id: string): Promise<ProductWithRelations | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*, images:product_images(*), variants:product_variants(*, color:product_colors(*), size:product_sizes(*))")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as ProductWithRelations | null;
  },

  async getBySlug(slug: string): Promise<ProductWithRelations | null> {
    const { data, error } = await supabase
      .from("products")
      .select("*, images:product_images(*), variants:product_variants(*, color:product_colors(*), size:product_sizes(*))")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as ProductWithRelations | null;
  },

  async upsert(payload: (TablesInsert<"products"> | TablesUpdate<"products">) & { id?: string }) {
    await assertAdminSession();

    const { id, ...rest } = payload;
    if (id) {
      const { data, error } = await supabase
        .from("products")
        .update(rest as TablesUpdate<"products">)
        .eq("id", id)
        .select("*");

      if (error) throw error;

      const rows = data ?? [];
      if (rows.length === 1) return rows[0] as unknown as Product;

      if (rows.length === 0) {
        const diagnosis = await diagnoseProductUpdate(id);
        if (diagnosis.result?.authenticated === false) {
          throw new ProductSaveError(
            "session_expired",
            "Sua sessão expirou. Entre novamente no painel antes de salvar o produto.",
          );
        }
        if (diagnosis.result?.is_admin === false) {
          throw new ProductSaveError(
            "admin_required",
            "A atualização foi bloqueada porque seu usuário não tem role admin.",
          );
        }
        if (diagnosis.result?.exists === false) {
          throw new ProductSaveError(
            "product_not_found",
            "Nenhum produto foi atualizado porque esse produto não existe mais no banco.",
          );
        }
        if (diagnosis.result?.exists === true) {
          throw new ProductSaveError(
            "rls_update_blocked",
            "O produto existe, mas a política RLS bloqueou o UPDATE para este usuário admin.",
          );
        }
        throw new ProductSaveError(
          "update_not_confirmed",
          diagnosis.error
            ? `Nenhuma linha foi atualizada e não foi possível diagnosticar o bloqueio: ${diagnosis.error.message}`
            : "Nenhuma linha foi atualizada. Verifique as permissões RLS antes de tentar novamente.",
        );
      }

      throw new ProductSaveError(
        "update_not_confirmed",
        "A atualização retornou mais de um produto. Salvamento cancelado para evitar inconsistência.",
      );
    }

    const { data, error } = await supabase
      .from("products")
      .insert(rest as TablesInsert<"products">)
      .select("*")
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      throw new ProductSaveError(
        "update_not_confirmed",
        "O produto foi enviado para criação, mas o banco não retornou a linha criada.",
      );
    }
    return data as unknown as Product;
  },

  async remove(id: string) {
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) throw error;
  },

  async addImage(image: Omit<ProductImage, "id">) {
    const { data, error } = await supabase.from("product_images").insert(image).select().single();
    if (error) throw error;
    return data as unknown as ProductImage;
  },

  async removeImage(id: string) {
    const { error } = await supabase.from("product_images").delete().eq("id", id);
    if (error) throw error;
  },

  async setPrimaryImage(productId: string, imageId: string) {
    const { error: clearError } = await supabase
      .from("product_images")
      .update({ is_primary: false })
      .eq("product_id", productId);
    if (clearError) throw clearError;
    const { error } = await supabase.from("product_images").update({ is_primary: true }).eq("id", imageId);
    if (error) throw error;
  },

  async removeVariant(id: string) {
    const { error } = await supabase.from("product_variants").delete().eq("id", id);
    if (error) throw error;
  },

  async upsertVariant(variant: (TablesInsert<"product_variants"> | TablesUpdate<"product_variants">) & { id?: string }) {
    const { id, ...rest } = variant;
    if (id) {
      const { data, error } = await supabase
        .from("product_variants")
        .update(rest as TablesUpdate<"product_variants">)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as ProductVariant;
    }

    // For inserts: if a variant with the same (product_id, color_id, size_id)
    // already exists, update it instead of failing on the unique constraint.
    // Postgres treats NULLs as distinct in unique indexes, so onConflict only
    // matches when both color_id and size_id are non-null.
    const payload = rest as TablesInsert<"product_variants">;
    const hasFullKey = !!payload.color_id && !!payload.size_id;
    if (hasFullKey) {
      const { data, error } = await supabase
        .from("product_variants")
        .upsert(payload, { onConflict: "product_id,color_id,size_id" })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as ProductVariant;
    }

    const { data, error } = await supabase
      .from("product_variants")
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    return data as unknown as ProductVariant;
  },
};

/**
 * Adapt the new Product shape to the legacy shape consumed by ProductCard/ProductGrid.
 * This keeps the existing UI working while we evolve the catalog.
 */
export const toLegacyProduct = (p: Product): LegacyProduct => ({
  id: p.id,
  name: p.name,
  price: Number(p.price),
  originalPrice: p.original_price ? Number(p.original_price) : null,
  image: p.image_url ?? "/placeholder.svg",
  category: p.category,
  badge: p.badge,
  slug: p.slug,
});

/**
 * Adapt a product fetched with relations (images + category_ref) to the legacy
 * shape, preferring the primary image and the relational category name.
 */
export const toLegacyProductWithRelations = (p: ProductWithRelations): LegacyProduct => {
  const images = p.images ?? [];
  const primary =
    images.find((i) => i.is_primary)?.image_url ??
    [...images].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0]?.image_url ??
    p.image_url ?? "/placeholder.svg";
  const variants = p.variants ?? [];
  const activeVariants = variants.filter((v) => v.is_active !== false);
  const variantPrices = activeVariants
    .map((v) => Number(v.price))
    .filter((n) => Number.isFinite(n) && n > 0);
  const productPrice = Number(p.price);
  const effectivePrice =
    Number.isFinite(productPrice) && productPrice > 0
      ? productPrice
      : variantPrices.length
        ? Math.min(...variantPrices)
        : 0;
  const totalStock = activeVariants.reduce(
    (sum, v) => sum + (Number(v.stock_quantity) || 0),
    0,
  );
  return {
    id: p.id,
    product_id: p.id,
    name: p.name,
    price: effectivePrice,
    originalPrice: p.original_price ? Number(p.original_price) : null,
    image: primary,
    category: p.category_ref?.name ?? p.category,
    badge: p.badge,
    slug: p.slug ?? null,
    is_bestseller: !!p.is_bestseller,
    total_stock: activeVariants.length ? totalStock : undefined,
  };
};

/**
 * Storage helper for product images. Returns the public URL.
 */
export const uploadProductImage = async (file: File): Promise<string> => {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `products/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("product-images").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("product-images").getPublicUrl(path);
  return data.publicUrl;
};

export const slugify = (input: string): string =>
  input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
